import { useEffect } from 'react';

import { useKFlags } from '@/hooks/use-k-flags';
import { useIsPreferencesLoaded, usePreferences } from '@/store/ServerStorage';

import {
  DARK_MODE_FLAG,
  DARK_MODE_MEDIA_QUERY,
  THEME_PREFERENCE_KEY,
} from './colorMode.constants';
import {
  applyThemeToDocument,
  clearStoredPreference,
  isThemePreference,
  writeStoredPreference,
} from './colorMode.utils';
import { useResolvedTheme, useThemeStore } from './themeStore';

/**
 * Keeps `<html data-kula-theme>` in step with the user's choice. Renders
 * nothing; mount it once inside the main app.
 *
 * - Flag on: applies the resolved theme and mirrors the preference to
 *   localStorage for the next page load's inline script.
 * - Flag off (explicitly `false`): removes the attribute and the mirror.
 * - Flag unknown (`undefined`, LaunchDarkly still loading): leaves the page as
 *   the inline script painted it, so an enabled user does not flash to light
 *   and back while flags load.
 */
export const ThemeSync = () => {
  const flags = useKFlags() as Record<string, unknown> | undefined;
  const flagValue = flags?.[DARK_MODE_FLAG];
  const isEnabled = flagValue === true;
  const isDisabled = flagValue === false;

  const preference = useThemeStore((state) => state.preference);
  const resolvedTheme = useResolvedTheme();

  const preferences = usePreferences() as Record<string, unknown>;
  const serverPreference = preferences?.[THEME_PREFERENCE_KEY];
  const isPreferencesLoaded = useIsPreferencesLoaded();

  // The server copy wins once it arrives: it is what the user chose on any
  // device, while the localStorage mirror only knows this browser.
  useEffect(() => {
    if (!isPreferencesLoaded || !isThemePreference(serverPreference)) return;
    if (serverPreference !== useThemeStore.getState().preference) {
      useThemeStore.getState().setPreference(serverPreference);
    }
  }, [isPreferencesLoaded, serverPreference]);

  useEffect(() => {
    if (!isEnabled || !window.matchMedia) return undefined;
    const query = window.matchMedia(DARK_MODE_MEDIA_QUERY);
    const onChange = (event: MediaQueryListEvent) =>
      useThemeStore.getState().setSystemPrefersDark(event.matches);
    useThemeStore.getState().setSystemPrefersDark(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [isEnabled]);

  useEffect(() => {
    if (isEnabled) {
      applyThemeToDocument(resolvedTheme);
      writeStoredPreference(preference);
    } else if (isDisabled) {
      applyThemeToDocument(null);
      clearStoredPreference();
    }
  }, [isEnabled, isDisabled, resolvedTheme, preference]);

  return null;
};
